import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import SelectField from '../common/SelectField.js';
import { shipmentService } from "../../services/shipmentService"
import { paymentService } from "../../services/ShipmentPaymentService.js"
import shipmentClientService from "../../services/ShipmentClientService.js"
import companySettingsService from "../../services/companySettingsService.js"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { MapPin, ArrowLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Plus } from "lucide-react"
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { getLocalDateString } from '../../utils/localDate.js'
import {
  formatHubBranchLabel,
  filterShipmentLocations,
  filterBranchLocations,
} from '../../utils/destinationType.js'
import {
  getSuggestionListClass,
  getSuggestionItemClass,
  handleSuggestionDropdownKeyDown,
  scrollHighlightedSuggestionIntoView,
} from '../../utils/suggestionDropdown.js'
import { useTranslation } from 'react-i18next';
import ShipmentMediaUploader from './ShipmentMediaUploader.js';
import GstVerificationField from './GstVerificationField.js';
import { deriveBookingGst } from './rcmGst.js';
import {
  PackageDimensionSummary,
  AddPackageDimensionModal,
  EditPackageDimensionModal,
  ViewPackageDimensionModal,
} from './package-dimension/index.js';
import {
  emptyParcel,
  emptyBusArticle,
  sumChargeableWeight,
  sumParcelQty,
  sumBusArticleWeight,
  calcFreightFromRate,
  calcBusFreight,
  parcelsForApi,
  parcelsFromApi,
  busArticlesFromApi,
  resolveCargoDefaults,
  calcInsuranceFromSettings,
  syncFormFromBusArticles,
  toNum,
  normalizeChargeMode,
  isShipmentChargeSplitMode,
  allocateShipmentChargeSplit,
  EXTRA_SHIPMENT_CHARGE_KEYS,
  EXTRA_CHARGE_FORM_FIELDS,
  PER_PARCEL_CHARGE_KEYS,
  resolvePerParcelChargeAmount,
  perParcelChargeTotal,
  perParcelUnitRate,
  shipmentParcelsAreGstExempt,
} from '../../utils/cargoParcelUtils.js';
import { DEFAULT_LABOUR_CHARGE_POLICY, labourCapturedAt, normalizeLabourChargePolicy } from '../../utils/labourChargePolicy.js';
import BusArticlePackagesForm from './BusArticlePackagesForm.js';
import { hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';

/** Optional Rate/KG from a location hub (receiver). Empty/missing → no override. */
const rateFromHub = (hub) => {
  if (!hub || hub.rate_per_kg === null || hub.rate_per_kg === undefined || hub.rate_per_kg === '') {
    return null
  }
  const n = Number(hub.rate_per_kg)
  if (!Number.isFinite(n) || n < 0) return null
  return String(n)
}

const MOBILE_STEPS = [
  { id: "location", label: "Location" },
  { id: "consignor", label: "Consignor" },
  { id: "consignee", label: "Consignee" },
  { id: "details", label: "Shipment" },
  { id: "payment", label: "Payment" },
]

const SHIPMENT_PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
]

const paymentOptionsForHub = (hub) => [
  hub?.allow_to_pay !== false && { value: "to_pay", label: "To Pay" },
  hub?.allow_paid !== false && { value: "paid", label: "Paid" },
  hub && hub.allow_on_account !== false && { value: "credit", label: "Credit" },
].filter(Boolean)

const normalizeShipmentPaymentMode = (mode) => {
  if (!mode) return ""
  const normalized = String(mode).toLowerCase().trim().replace(/\s+/g, "_")
  const aliases = {
    online_transfer: "online",
    bank: "bank_transfer",
    card_payment: "online",
    card: "online",
  }
  return aliases[normalized] || normalized
}

const resolveShipmentPaymentMode = (shipment, paymentHistory = []) => {
  const fromShipment = normalizeShipmentPaymentMode(shipment?.payment_mode)
  if (fromShipment) return fromShipment

  const history = Array.isArray(paymentHistory) ? paymentHistory : []
  const bookingPayment = history.find(
    (payment) => payment?.payment_mode && String(payment.type || "").toLowerCase() === "booking",
  )
  const paymentWithMode = bookingPayment || history.find((payment) => payment?.payment_mode)
  return normalizeShipmentPaymentMode(paymentWithMode?.payment_mode)
}

const EditShipment = () => {
  const { t } = useTranslation();
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {})
  const ewaybillFeatureOn = Boolean(companyAccess.ewaybill)
  const [gstVerificationOn, setGstVerificationOn] = useState(
    Boolean(companyAccess.gst_verification)
  )
  const [loading, setLoading] = useState(true)
  const [showCustomArticleType, setShowCustomArticleType] = useState(false)
  const [customArticleType, setCustomArticleType] = useState("")
  const [saving, setSaving] = useState(false)
  const [consignerLocations, setConsignerLocations] = useState([])
  const [consigneeLocations, setConsigneeLocations] = useState([])
  const [consigneeLocationSearch, setConsigneeLocationSearch] = useState("")
  const [showConsigneeDropdown, setShowConsigneeDropdown] = useState(false)
  const consigneeRef = useRef(null)
  const paidPaymentFieldsRef = useRef(null)
  const parcelImagesRef = useRef(null)
  const prevModeOfPaymentRef = useRef("to_pay")
  const insuranceFreightSyncedRef = useRef(null)

  const [showConsignorExtra, setShowConsignorExtra] = useState(false)
  const [showConsigneeExtra, setShowConsigneeExtra] = useState(false)
  const [showParcelImages, setShowParcelImages] = useState(false)
  const [showGSTDetails, setShowGSTDetails] = useState(false)
  const [consigneeHighlightIndex, setConsigneeHighlightIndex] = useState(-1)

  const [mobileStep, setMobileStep] = useState(0)
  const mobileStepRef = useRef(0)
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches
  )

  useEffect(() => {
    mobileStepRef.current = mobileStep
  }, [mobileStep])

  const [useShipmentClient, setUseShipmentClient] = useState(false)
  const [shipmentClients, setShipmentClients] = useState([])
  const [clientSearch, setClientSearch] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientHighlightIndex, setClientHighlightIndex] = useState(-1)

  const [consignorSuggestions, setConsignorSuggestions] = useState([])
  const [showConsignorSuggestions, setShowConsignorSuggestions] = useState(false)
  const [consignorSuggestionsLoading, setConsignorSuggestionsLoading] = useState(false)
  const [consignorSuggestionHighlightIndex, setConsignorSuggestionHighlightIndex] = useState(-1)
  const consignorDebounceRef = useRef(null)

  const [consigneeSuggestions, setConsigneeSuggestions] = useState([])
  const [showConsigneeSuggestions, setShowConsigneeSuggestions] = useState(false)
  const [consigneeSuggestionsLoading, setConsigneeSuggestionsLoading] = useState(false)
  const [consigneeSuggestionHighlightIndex, setConsigneeSuggestionHighlightIndex] = useState(-1)
  const consigneeDebounceRef = useRef(null)
  const skipCreditRematchRef = useRef("")

  const [chargesExpanded, setChargesExpanded] = useState(false)
  const [shipmentIdType, setShipmentIdType] = useState('auto')
  const [consignmentNo, setConsignmentNo] = useState(null)

  const [showEwayBillModal, setShowEwayBillModal] = useState(false)
  const [ewayBills, setEwayBills] = useState([""])
  const [submittedEwayBills, setSubmittedEwayBills] = useState([])
  const [existingEwayBills, setExistingEwayBills] = useState([])
  const [loadingEwayBills, setLoadingEwayBills] = useState(false)
  const [ewaybillApiEnabled, setEwaybillApiEnabled] = useState(false)
  const [showUpdateVehicleModal, setShowUpdateVehicleModal] = useState(false)
  const [ewayActionLoading, setEwayActionLoading] = useState(false)
  const [selectedEwayBill, setSelectedEwayBill] = useState(null)
  const [updateVehicleForm, setUpdateVehicleForm] = useState({
    vehicleNo: '',
    reasonRem: 'Vehicle Update',
  })

  // Store original charges to track if Agent reduced them
  const [bookedPaymentMode, setBookedPaymentMode] = useState("")
  const [originalCharges, setOriginalCharges] = useState({
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    late_charges: "",
    docket_fee: "10",
    other_charges: "",
    discount: "",
  })

  const [formData, setFormData] = useState({
    consigner_name: "",
    consigner_address: "",
    consigner_location: "",
    consigner_contact: "",
    consigner_email: "",
    consigner_gst: "",
    consignee_name: "",
    consignee_address: "",
    consignee_location: "",
    consignee_contact: "",
    consignee_email: "",
    consignee_gst: "",
    invoice_number: "",
    booking_date: getLocalDateString(),
    no_of_articles: 1,
    article_type: "",
    article_amount: "",
    weight: "",
    shipment_value: "",
    freight_charges: "",
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    late_charges: "",
    docket_fee: "10",
    other_charges: "",
    insurance_charges: "",
    discount: "",
    rate_per_kg: "",
    loading_charges: "",
    unloading_charges: "",
    toll_charges: "",
    fuel_surcharge: "",
    packing_charges: "",
    delivery_type: "hub_delivery",
    mode_of_payment: "to_pay",
    payment_mode: "",
    pickup_type: "partial_pickup",
    parcel_detail: "",
    parcel_image_1: null,
    parcel_image_2: null,
    paid_amount: "",
    sgst_percentage: "",
    cgst_percentage: "",
    igst_percentage: "",
    status: "Booked",
  })

  // RCM (Reverse Charge Mechanism) company setting
  const [companyRcm, setCompanyRcm] = useState({ enabled: false, igst: 0, cgst: 0, sgst: 0 })
  const [companyGstInfo, setCompanyGstInfo] = useState({ gst_number: "", state: "" })
  const [senderGstLookup, setSenderGstLookup] = useState({ status: 'idle', data: null, error: '' })
  const [receiverGstLookup, setReceiverGstLookup] = useState({ status: 'idle', data: null, error: '' })
  const onSenderGstLookup = useCallback((next) => {
    setSenderGstLookup((prev) => (
      prev.status === next.status && prev.data === next.data && prev.error === next.error
        ? prev
        : next
    ))
  }, [])
  const onReceiverGstLookup = useCallback((next) => {
    setReceiverGstLookup((prev) => (
      prev.status === next.status && prev.data === next.data && prev.error === next.error
        ? prev
        : next
    ))
  }, [])
  const [isCargoMode, setIsCargoMode] = useState(false)
  const [busFreightByWeight, setBusFreightByWeight] = useState(false)
  const [chargeDefaults, setChargeDefaults] = useState(resolveCargoDefaults())
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY)
  const [parcels, setParcels] = useState([emptyBusArticle(1)])
  const parcelCountForCharges = useMemo(() => sumParcelQty(parcels), [parcels])
  const gstExempt = useMemo(() => shipmentParcelsAreGstExempt(parcels), [parcels])
  const loadingAtBooking = labourCapturedAt(labourPolicy, 'loading', 'booking')
  const unloadingAtBooking = labourCapturedAt(labourPolicy, 'unloading', 'booking')
  const chargeSplitMode = isShipmentChargeSplitMode(chargeDefaults, labourPolicy)
  const articleFreightBase = useMemo(() => {
    if (isCargoMode) {
      return calcFreightFromRate(toNum(formData.rate_per_kg), sumChargeableWeight(parcels))
    }
    return calcBusFreight(parcels, formData.rate_per_kg, busFreightByWeight)
  }, [isCargoMode, parcels, formData.rate_per_kg, busFreightByWeight])
  const chargeSplit = useMemo(() => {
    const formAmounts = {}
    EXTRA_SHIPMENT_CHARGE_KEYS.forEach((key) => {
      if (key === 'loading' && !loadingAtBooking) return
      if (key === 'unloading' && !unloadingAtBooking) return
      formAmounts[key] = formData[EXTRA_CHARGE_FORM_FIELDS[key]]
    })
    return allocateShipmentChargeSplit(chargeDefaults, articleFreightBase, formAmounts, labourPolicy)
  }, [
    chargeDefaults,
    articleFreightBase,
    formData.docket_fee,
    formData.handling_charges,
    formData.door_pickup_charges,
    formData.delivery_charges,
    formData.loading_charges,
    formData.unloading_charges,
    formData.toll_charges,
    formData.fuel_surcharge,
    formData.packing_charges,
    loadingAtBooking,
    unloadingAtBooking,
    labourPolicy,
  ])

  const resolveChargeAmount = (chargeKey, fieldName) =>
    resolvePerParcelChargeAmount({
      chargeKey,
      unitRate: formData[fieldName],
      parcelCount: parcelCountForCharges,
      chargeDefaults,
    })

  const [showAddPackageModal, setShowAddPackageModal] = useState(false)
  const [addPackageAppendMode, setAddPackageAppendMode] = useState(false)
  const [showEditPackageModal, setShowEditPackageModal] = useState(false)
  const [showViewPackageModal, setShowViewPackageModal] = useState(false)

  const [imagePreview, setImagePreview] = useState({ img1: null, img2: null })
  const [existingImages, setExistingImages] = useState({ img1: null, img2: null })
  const [shipmentMediaFiles, setShipmentMediaFiles] = useState([])
  const [existingMedia, setExistingMedia] = useState([])
  const [removedMediaIds, setRemovedMediaIds] = useState([])

  useEffect(() => {
    const onMediaError = (e) => {
      if (e?.detail) Alert.error(String(e.detail))
    }
    window.addEventListener('shipment-media-error', onMediaError)
    return () => window.removeEventListener('shipment-media-error', onMediaError)
  }, [])

  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    consigner_name:     (v) => validators.required(v, 'Consignor name'),
    consigner_contact:  (v) => validators.required(v, 'Consignor contact') || validators.contact(v),
    consigner_location: (v) => validators.required(v, 'From location'),
    consigner_email:    (v) => v ? validators.email(v) : '',
    consigner_gst:      (v) => v ? validators.gst(v) : '',
    consignee_name:     (v) => validators.required(v, 'Consignee name'),
    consignee_contact:  (v) => validators.required(v, 'Consignee contact') || validators.contact(v),
    consignee_location: (v) => validators.required(v, 'Destination'),
    consignee_email:    (v) => v ? validators.email(v) : '',
    consignee_gst:      (v) => v ? validators.gst(v) : '',
    article_type:       () => '',
    no_of_articles:     () => '',
    article_amount:     () => '',
  }

  const handleBusArticlesChange = (nextArticles) => {
    setParcels(nextArticles)
    setFormData((prev) => ({ ...prev, ...syncFormFromBusArticles(nextArticles) }))
  }

  // Check if user is Agent role
  const roleName = user?.role || user?.role_name
  const isAgentRole = roleName === "Agent"
  const canEditShipmentStatus = roleName === "Admin" || roleName === "System Admin"
  const isCreditClientShipment = Boolean(formData.shipment_client_id)
  const editStatusOptions = [
    "Booked",
    "In Transit",
    "Dispatched",
    "Received",
    "Delivered",
    "Rejected",
    "Returned",
  ]

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const mobileStepPanelClass = (step) => {
    if (!isMobileLayout) return ""
    return mobileStep === step
      ? "flex flex-col flex-1 min-h-0 overflow-y-auto"
      : "hidden"
  }

  const mobileStepPanelClassMulti = (...steps) => {
    if (!isMobileLayout) return ""
    if (!steps.includes(mobileStep)) return "hidden"
    if (mobileStep === 4) {
      return "block w-full min-w-0 overflow-visible pb-28"
    }
    return "flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
  }

  const mobilePaymentPanelVisibility = () => {
    if (!isMobileLayout) return ""
    return mobileStep === 4 ? "block w-full min-w-0 shrink-0" : "hidden"
  }

  const validateMobileStep = (step) => {
    if (step === 0) {
      if (!formData.consigner_location?.trim()) {
        Alert.error("Please select a sender location")
        return false
      }
      if (!formData.consignee_location?.trim()) {
        Alert.error("Please select a receiver location")
        return false
      }
      return true
    }
    if (step === 1) {
      return validate(formData, {
        consigner_name: validationRules.consigner_name,
        consigner_contact: validationRules.consigner_contact,
      })
    }
    if (step === 2) {
      return validate(formData, {
        consignee_name: validationRules.consignee_name,
        consignee_contact: validationRules.consignee_contact,
      })
    }
    if (step === 3) {
      return validate(formData, {
        article_type: validationRules.article_type,
        no_of_articles: validationRules.no_of_articles,
        article_amount: validationRules.article_amount,
      })
    }
    return true
  }

  const goToNextMobileStep = () => {
    const step = mobileStepRef.current
    if (!validateMobileStep(step)) return
    const nextStep = Math.min(step + 1, MOBILE_STEPS.length - 1)
    mobileStepRef.current = nextStep
    setMobileStep(nextStep)
  }

  const handleFormSubmit = (e) => {
    e?.preventDefault?.()
    if (isMobileLayout) {
      const step = mobileStepRef.current
      if (step < MOBILE_STEPS.length - 1) {
        if (!validateMobileStep(step)) return
        const nextStep = step + 1
        mobileStepRef.current = nextStep
        setMobileStep(nextStep)
        return
      }
    }
    handleSubmit(e)
  }

  const handleMobilePrimaryAction = () => {
    if (mobileStepRef.current < MOBILE_STEPS.length - 1) {
      goToNextMobileStep()
      return
    }
    handleSubmit({ preventDefault: () => {} })
  }

  const handleMobileStepTabClick = (idx) => {
    const current = mobileStepRef.current
    if (idx === current) return
    if (idx < current) {
      mobileStepRef.current = idx
      setMobileStep(idx)
      return
    }
    for (let step = current; step < idx; step += 1) {
      if (!validateMobileStep(step)) return
    }
    mobileStepRef.current = idx
    setMobileStep(idx)
  }

  const goToPrevMobileStep = () => {
    setMobileStep((s) => Math.max(s - 1, 0))
  }

  useEffect(() => {
    fetchShipment()
    fetchLocationHubs()
    fetchCompanySettings()
  }, [shipmentId])

  const fetchCompanySettings = async () => {
    try {
      const res = await companySettingsService.getSettings()
      if (res?.data?.rcm) {
        setCompanyRcm({ enabled: false, igst: 0, cgst: 0, sgst: 0, ...res.data.rcm })
      }
      setCompanyGstInfo({
        gst_number: res?.data?.company_gst_number || "",
        state: res?.data?.company_state || "",
      })
      if (typeof res?.data?.gst_verification === 'boolean') {
        setGstVerificationOn(res.data.gst_verification)
      }
      const insurance = res?.data?.shipment_charge_defaults?.insurance
        || res?.data?.shipment_insurance_charge
        || {}
      const defaults = resolveCargoDefaults(
        res?.data?.shipment_charge_defaults || res?.data?.cargo_charge_defaults,
      )
      defaults.insurance = {
        enabled: !!insurance.enabled,
        amount: Number.parseFloat(insurance.amount) || 0,
        mode: insurance.mode === 'percent' ? 'percent' : 'fixed',
      }
      setChargeDefaults(defaults)
      setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy))
      setBusFreightByWeight(!!res?.data?.bus_freight_by_weight)
    } catch (error) {
      // Settings unavailable -> keep manual GST / no insurance field
    }
  }

  const getHubState = (hubId, locationName = null) => {
    const list = [...consignerLocations, ...consigneeLocations]
    if (hubId) {
      const byId = list.find((h) => String(h.id) === String(hubId))
      if (byId?.state) return byId.state
    }
    if (locationName) {
      const name = locationName.trim().toLowerCase()
      const byName = list.find((h) => (h.location || "").trim().toLowerCase() === name)
      if (byName?.state) return byName.state
    }
    return null
  }

  const deriveRcmGst = () => deriveBookingGst({
    rcmEnabled: !!companyRcm.enabled,
    rcm: companyRcm,
    senderGst: formData.consigner_gst,
    receiverGst: formData.consignee_gst,
    mode: formData.mode_of_payment,
    companyGst: companyGstInfo.gst_number,
    senderState: getHubState(formData.sender_location_hub_id, formData.consigner_location),
    receiverState: getHubState(formData.receiver_location_hub_id, formData.consignee_location),
    verificationOn: gstVerificationOn,
    senderLookup: senderGstLookup,
    receiverLookup: receiverGstLookup,
  })

  const rcmActive = !gstExempt && deriveRcmGst().override

  useEffect(() => {
    if (!rcmActive) return
    const d = deriveRcmGst()
    const s = String(d.sgst)
    const c = String(d.cgst)
    const i = String(d.igst)
    setFormData((prev) => {
      if (
        prev.sgst_percentage === s &&
        prev.cgst_percentage === c &&
        prev.igst_percentage === i
      ) {
        return prev
      }
      return { ...prev, sgst_percentage: s, cgst_percentage: c, igst_percentage: i }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rcmActive,
    gstVerificationOn,
    senderGstLookup,
    receiverGstLookup,
    companyRcm,
    companyGstInfo,
    formData.consigner_gst,
    formData.consignee_gst,
    formData.mode_of_payment,
    formData.sender_location_hub_id,
    formData.receiver_location_hub_id,
    formData.consigner_location,
    formData.consignee_location,
    consignerLocations,
    consigneeLocations,
  ])

  useEffect(() => {
    const prev = prevModeOfPaymentRef.current
    prevModeOfPaymentRef.current = formData.mode_of_payment

    const isPaid =
      formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance"
    const wasPaid = prev === "paid" || prev === "paid_balance"

    if (isPaid && !wasPaid) {
      if (!isMobileLayout) {
        setShowParcelImages(true)
      }
      if (!isMobileLayout) {
        const timer = setTimeout(() => {
          paidPaymentFieldsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          })
          setTimeout(() => {
            parcelImagesRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }, 350)
        }, 80)
        return () => clearTimeout(timer)
      }
    }
  }, [formData.mode_of_payment, isMobileLayout])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (consigneeRef.current && !consigneeRef.current.contains(event.target)) {
        setShowConsigneeDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const filteredConsigneeLocations = consigneeLocations.filter((loc) => {
    const label = formatHubBranchLabel(loc).toLowerCase()
    const q = consigneeLocationSearch.toLowerCase()
    return label.includes(q) || (loc.location || "").toLowerCase().includes(q)
  })
  const internalHubDispatchControls = hasInternalHubDispatchControls(user)
  const bookingHub = consignerLocations.find((hub) =>
    String(hub.id) === String(formData.sender_location_hub_id || user?.location_hub_id),
  )
  const creditAllowed = !internalHubDispatchControls || Boolean(bookingHub && bookingHub.allow_on_account !== false)
  const bookingHubId = formData.sender_location_hub_id || user?.location_hub_id || bookingHub?.id

  useEffect(() => {
    if (!creditAllowed) {
      setShipmentClients([])
      return
    }
    if (internalHubDispatchControls && !bookingHubId) {
      setShipmentClients([])
      return
    }
    fetchShipmentClients(bookingHubId)
  }, [creditAllowed, bookingHubId, internalHubDispatchControls])
  const paymentStatusOptions = useMemo(() => {
    const options = internalHubDispatchControls
      ? paymentOptionsForHub(bookingHub)
      : [
        { value: "to_pay", label: "To Pay" },
        { value: "credit", label: "Credit" },
        { value: "paid", label: "Paid" },
      ]
    return options
  }, [internalHubDispatchControls, bookingHub, useShipmentClient])

  const canAdminEditPaymentStatus = roleName === "Admin" || roleName === "System Admin"
  const editPaymentStatusOptions = canAdminEditPaymentStatus
    ? [
        { value: "to_pay", label: "To Pay" },
        { value: "paid", label: "Paid" },
        { value: "credit", label: "Credit" },
      ]
    : paymentStatusOptions

  useEffect(() => {
    if (useShipmentClient) return
    if (canAdminEditPaymentStatus) return
    if (!internalHubDispatchControls || paymentStatusOptions.length === 0) return
    if (!paymentStatusOptions.some((option) => option.value === formData.mode_of_payment)) {
      setFormData((prev) => ({ ...prev, mode_of_payment: paymentStatusOptions[0].value }))
    }
  }, [internalHubDispatchControls, formData.mode_of_payment, paymentStatusOptions, formData.sender_location_hub_id, bookingHub?.id, bookingHub?.allow_to_pay, bookingHub?.allow_paid, bookingHub?.allow_on_account, useShipmentClient, canAdminEditPaymentStatus])

  useEffect(() => {
    if (!useShipmentClient) return
    if (canAdminEditPaymentStatus && formData.mode_of_payment !== "credit") return
    if (formData.mode_of_payment === "credit") return
    setFormData((prev) => ({
      ...prev,
      mode_of_payment: "credit",
      paid_amount: "",
      payment_mode: "",
    }))
  }, [useShipmentClient, formData.mode_of_payment, canAdminEditPaymentStatus])

  const filteredClients = shipmentClients.filter(
    (client) =>
      client.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.company_name.toLowerCase().includes(clientSearch.toLowerCase()),
  )

  const isValidEway = (value) => /^\d{12}$/.test(value)

  const handleCloseEwayBillModal = () => {
    setShowEwayBillModal(false)
    setEwayBills([""])
  }

  const handleSubmitEwayBills = async () => {
    const validBills = ewayBills.filter(isValidEway)
    if (validBills.length === 0) {
      Alert.error("Please enter at least one valid E-way bill")
      return
    }

    const allBills = [...existingEwayBills.map(b => b.eway_bill_number), ...submittedEwayBills, ...validBills]
    const uniqueBills = [...new Set(allBills)]

    if (uniqueBills.length !== allBills.length) {
      Alert.error("Duplicate E-way bill numbers found")
      return
    }

    setSubmittedEwayBills(prev => [...prev, ...validBills])
    Alert.success(`${validBills.length} E-way bill(s) added successfully`)
    handleCloseEwayBillModal()
  }

  const handleViewEwayBills = () => {
    const totalBills = existingEwayBills.length + submittedEwayBills.length
    if (totalBills === 0) {
      Alert.info("No E-way bills added yet")
      return
    }

    const existingNumbers = existingEwayBills.map(b => b.eway_bill_number).join(", ")
    const newNumbers = submittedEwayBills.join(", ")
    const message = `E-way Bills (${totalBills}):\n${existingNumbers}${newNumbers ? '\nNew: ' + newNumbers : ''}`

    Alert.info(message, { duration: 5000 })
  }

  const handleDeleteEwayBill = async (ewayBillId, ewayBillNumber) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete E-way bill ${ewayBillNumber}? This action cannot be undone.`,
      'Confirm Delete'
    )
    
    if (!confirmed) {
      return
    }
    
    try {
      setLoadingEwayBills(true)
      await shipmentService.deleteEwayBill(ewayBillId)
      Alert.success("E-way bill deleted successfully")
      await fetchEwayBills()
    } catch (error) {
      console.error("Error deleting e-way bill:", error)
      Alert.error(error.response?.data?.error || "Failed to delete E-way bill")
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const fetchEwayBills = async () => {
    try {
      setLoadingEwayBills(true)
      const response = await shipmentService.getEwayBills(shipmentId)
      setExistingEwayBills(response.data.eway_bills || [])
      if (typeof response.data.ewaybill_api_enabled === 'boolean') {
        setEwaybillApiEnabled(response.data.ewaybill_api_enabled)
      }
    } catch (error) {
      console.error("Error fetching e-way bills:", error)
      setExistingEwayBills([])
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const handleUpdateEwayVehicle = async () => {
    if (!selectedEwayBill?.id || !updateVehicleForm.vehicleNo) {
      Alert.error('Vehicle number is required')
      return
    }
    try {
      setEwayActionLoading(true)
      await shipmentService.updateEwayBillVehicle(selectedEwayBill.id, {
        vehicleNo: updateVehicleForm.vehicleNo.replace(/\s/g, '').toUpperCase(),
        reasonRem: updateVehicleForm.reasonRem || 'Vehicle Update',
      })
      Alert.success('Vehicle / Part-B updated')
      setShowUpdateVehicleModal(false)
      setSelectedEwayBill(null)
      await fetchEwayBills()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update vehicle')
    } finally {
      setEwayActionLoading(false)
    }
  }

  const handleCancelEwayBill = async (bill) => {
    const confirmed = await Alert.confirm(
      `Cancel e-way bill ${bill.eway_bill_number}? GST allows cancel only within ~24 hours of generation.`,
      'Confirm Cancel'
    )
    if (!confirmed) return
    try {
      setLoadingEwayBills(true)
      await shipmentService.cancelEwayBill(bill.id, {
        cancelRmrk: 'Order Cancelled',
        cancelRsnCode: 2,
      })
      Alert.success('E-way bill cancelled')
      await fetchEwayBills()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to cancel e-way bill')
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const fetchShipmentClients = async (hubId) => {
    try {
      const response = await shipmentClientService.getForBooking(hubId)
      setShipmentClients(response.credit_allowed === false ? [] : (response.data || []))
    } catch (error) {
      console.error("Error fetching shipment clients:", error)
      setShipmentClients([])
    }
  }

  const handleClientSelect = (client) => {
    setSelectedClient(client)
    setFormData({
      ...formData,
      consigner_name: client.full_name,
      consigner_address: client.address,
      consigner_contact: client.contact,
      consigner_email: client.email,
      consigner_gst: client.gst_number || "",
      shipment_client_id: client.id,
      mode_of_payment: "credit",
      paid_amount: "",
      payment_mode: "",
    })
    setShowClientDropdown(false)
    setClientSearch("")
    setClientHighlightIndex(-1)
  }

  const handleUseClientChange = (checked) => {
    setUseShipmentClient(checked)
    if (checked) {
      skipCreditRematchRef.current = ""
      setFormData((prev) => ({
        ...prev,
        mode_of_payment: "credit",
        paid_amount: "",
        payment_mode: "",
      }))
      return
    }
    skipCreditRematchRef.current = String(formData.consigner_contact || "").replace(/\D/g, "")
    setSelectedClient(null)
    setClientSearch("")
    setShowClientDropdown(false)
    setClientHighlightIndex(-1)
  }

  useEffect(() => {
    if (canAdminEditPaymentStatus && formData.mode_of_payment !== "credit") return
    if (useShipmentClient || selectedClient) return
    const digits = String(formData.consigner_contact || "").replace(/\D/g, "")
    if (digits.length !== 10) return
    if (skipCreditRematchRef.current === digits) return
    const match = shipmentClients.find(
      (client) => String(client.contact || "").replace(/\D/g, "") === digits,
    )
    if (!match) return
    setUseShipmentClient(true)
    handleClientSelect(match)
  }, [formData.consigner_contact, formData.mode_of_payment, shipmentClients, useShipmentClient, selectedClient, canAdminEditPaymentStatus])

  const fetchConsignorSuggestions = async (searchQuery) => {
    if (consignorDebounceRef.current) {
      clearTimeout(consignorDebounceRef.current)
    }

    const digits = String(searchQuery || "").replace(/\D/g, "")
    if (digits.length < 10) {
      setConsignorSuggestions([])
      setShowConsignorSuggestions(false)
      setConsignorSuggestionsLoading(false)
      return
    }

    setShowConsignorSuggestions(true)
    setConsignorSuggestionsLoading(true)

    consignorDebounceRef.current = setTimeout(async () => {
      try {
        const response = await shipmentService.getCustomerSuggestions(digits)
        setConsignorSuggestions(
          shipmentService.mergeCustomerSuggestions(
            response.data?.data?.senders,
            response.data?.data?.receivers,
          )
        )
      } catch (error) {
        console.error("Error fetching consignor suggestions:", error)
        setConsignorSuggestions([])
      } finally {
        setConsignorSuggestionsLoading(false)
      }
    }, 400)
  }

  const fetchConsigneeSuggestions = async (searchQuery) => {
    if (consigneeDebounceRef.current) {
      clearTimeout(consigneeDebounceRef.current)
    }

    const digits = String(searchQuery || "").replace(/\D/g, "")
    if (digits.length < 10) {
      setConsigneeSuggestions([])
      setShowConsigneeSuggestions(false)
      setConsigneeSuggestionsLoading(false)
      return
    }

    setShowConsigneeSuggestions(true)
    setConsigneeSuggestionsLoading(true)

    consigneeDebounceRef.current = setTimeout(async () => {
      try {
        const response = await shipmentService.getCustomerSuggestions(digits)
        const contactKey = digits.slice(-10)

        setFormData((prev) => {
          const senderKey = String(prev.consigner_contact || "").replace(/\D/g, "").slice(-10)
          const currentSender =
            senderKey === contactKey && (prev.consigner_name || prev.consigner_contact)
              ? [{
                  name: prev.consigner_name || "",
                  contact: prev.consigner_contact || "",
                  email: prev.consigner_email || "",
                  address: prev.consigner_address || "",
                  gst: prev.consigner_gst || "",
                }]
              : []

          setConsigneeSuggestions(
            shipmentService.mergeCustomerSuggestions(
              currentSender,
              response.data?.data?.receivers,
              response.data?.data?.senders,
            )
          )

          const consigneeNameEmpty = !String(prev.consignee_name || "").trim()
          if (senderKey === contactKey && consigneeNameEmpty && prev.consigner_name) {
            if (prev.consigner_address || prev.consigner_email || prev.consigner_gst) {
              setShowConsigneeExtra(true)
            }
            return {
              ...prev,
              consignee_name: prev.consigner_name || "",
              consignee_contact: prev.consigner_contact || contactKey,
              consignee_address: prev.consigner_address || "",
              consignee_email: prev.consigner_email || "",
              consignee_gst: prev.consigner_gst || "",
            }
          }
          return prev
        })
      } catch (error) {
        console.error("Error fetching consignee suggestions:", error)
        setConsigneeSuggestions([])
      } finally {
        setConsigneeSuggestionsLoading(false)
      }
    }, 400)
  }

  const handleConsignorSuggestionSelect = (suggestion) => {
    setFormData((prev) => {
      const consigneeEmpty =
        !String(prev.consignee_contact || "").trim() &&
        !String(prev.consignee_name || "").trim()

      const next = {
        ...prev,
        consigner_name: suggestion.name || "",
        consigner_contact: suggestion.contact || "",
        consigner_address: suggestion.address || "",
        consigner_email: suggestion.email || "",
        consigner_gst: suggestion.gst || "",
      }

      if (consigneeEmpty) {
        next.consignee_name = suggestion.name || ""
        next.consignee_contact = suggestion.contact || ""
        next.consignee_address = suggestion.address || ""
        next.consignee_email = suggestion.email || ""
        next.consignee_gst = suggestion.gst || ""
        if (suggestion.address || suggestion.email || suggestion.gst) {
          setShowConsigneeExtra(true)
        }
      }
      return next
    })
    if (suggestion.address || suggestion.email || suggestion.gst) {
      setShowConsignorExtra(true)
    }
    setShowConsignorSuggestions(false)
    setConsignorSuggestions([])
    setConsignorSuggestionHighlightIndex(-1)
  }

  const handleConsigneeSuggestionSelect = (suggestion) => {
    setFormData({
      ...formData,
      consignee_name: suggestion.name || "",
      consignee_contact: suggestion.contact || "",
      consignee_address: suggestion.address || "",
      consignee_email: suggestion.email || "",
      consignee_gst: suggestion.gst || "",
    })
    if (suggestion.address || suggestion.email || suggestion.gst) {
      setShowConsigneeExtra(true)
    }
    setShowConsigneeSuggestions(false)
    setConsigneeSuggestions([])
    setConsigneeSuggestionHighlightIndex(-1)
  }

  const fetchShipment = async () => {
    try {
      setLoading(true)
      const [shipmentResponse, paymentHistoryResponse] = await Promise.all([
        shipmentService.getShipment(shipmentId),
        paymentService.getPaymentHistory(shipmentId).catch(() => null),
      ])
      const shipment = shipmentResponse.data.shipment || shipmentResponse.data.data
      if (shipment?.is_deleted) {
        Alert.error('This shipment is deleted and cannot be edited.')
        navigate(`/shipments/view/${shipmentId}`)
        return
      }
      if (shipment?.can_edit === false) {
        Alert.error(shipment.edit_block_reason || 'This shipment is on an ML sheet. Only Admin can edit it.')
        navigate(`/shipments/view/${shipmentId}`)
        return
      }
      const paymentHistory =
        paymentHistoryResponse?.data?.data?.payment_history
        || paymentHistoryResponse?.data?.payment_history
        || []

      const mappedShipment = {
        ...shipment,
        consigner_name: shipment.consigner_name || shipment.sender_name,
        consigner_address: shipment.consigner_address || shipment.sender_address,
        consigner_location: shipment.consigner_location || shipment.sender_location,
        sender_location_hub_id: shipment.sender_location_hub_id || "",
        consigner_contact: shipment.consigner_contact || shipment.sender_contact,
        consigner_email: shipment.consigner_email || shipment.sender_email,
        consigner_gst: shipment.consigner_gst || shipment.sender_gst,
        consignee_name: shipment.consignee_name || shipment.receiver_name,
        consignee_address: shipment.consignee_address || shipment.receiver_address,
        consignee_location: shipment.consignee_location || shipment.receiver_location,
        receiver_location_hub_id: shipment.receiver_location_hub_id || "",
        consignee_contact: shipment.consignee_contact || shipment.receiver_contact,
        consignee_email: shipment.consignee_email || shipment.receiver_email,
        consignee_gst: shipment.consignee_gst || shipment.receiver_gst,
        invoice_number: shipment.invoice_number || "",
        booking_date: shipment.created_at ? shipment.created_at.split("T")[0] : getLocalDateString(),
        payment_mode: resolveShipmentPaymentMode(shipment, paymentHistory),
        paid_amount: shipment.paid_amount ?? "",
        insurance_charges: shipment.insurance_charges ?? "",
        rate_per_kg: shipment.rate_per_kg ?? "",
        loading_charges: shipment.loading_charges ?? "",
        unloading_charges: shipment.unloading_charges ?? "",
        toll_charges: shipment.toll_charges ?? "",
        fuel_surcharge: shipment.fuel_surcharge ?? "",
        packing_charges: shipment.packing_charges ?? "",
      }

      prevModeOfPaymentRef.current = shipment.mode_of_payment || "to_pay"
      setBookedPaymentMode(String(shipment.mode_of_payment || "").toLowerCase())

      const cargoShipment = (shipment.shipment_type || "bus") === "cargo"
      setIsCargoMode(cargoShipment)
      const loadedParcels = cargoShipment
        ? parcelsFromApi(shipment.parcels)
        : busArticlesFromApi(shipment.parcels, shipment)
      const parcelQty = sumParcelQty(loadedParcels)
      setParcels(loadedParcels)

      const toUnitRate = (stored, chargeKey) => {
        if (!PER_PARCEL_CHARGE_KEYS.has(chargeKey)) return stored ?? ''
        const unit = perParcelUnitRate(stored, parcelQty)
        return unit > 0 ? String(unit) : (stored ?? '')
      }

      setFormData({
        ...mappedShipment,
        loading_charges: toUnitRate(shipment.loading_charges, 'loading'),
        unloading_charges: toUnitRate(shipment.unloading_charges, 'unloading'),
        door_pickup_charges: toUnitRate(shipment.door_pickup_charges, 'door_pickup'),
        delivery_charges: toUnitRate(shipment.delivery_charges, 'door_delivery'),
      })

      // Store shipment ID type for display badge
      setShipmentIdType(shipment.shipment_id_type || 'auto')
      setConsignmentNo(shipment.consignment_no || null)

      // Store original charges for Agent role validation
      setOriginalCharges({
        handling_charges: shipment.handling_charges || "",
        delivery_charges: shipment.delivery_charges || "",
        door_pickup_charges: shipment.door_pickup_charges || "",
        late_charges: shipment.late_charges || "",
        docket_fee: shipment.docket_fee || "10",
        other_charges: shipment.other_charges || "",
        discount: shipment.discount || "",
      })

      // Initialize custom article type if value doesn't match known options
      const knownTypes = ["BOX","BRIEFCASE","PACKAGE","ENVELOPE","PARCEL","CARTON","CRATE","DRUM","BAG","PALLET"]
      if (shipment.article_type && !knownTypes.includes(shipment.article_type.toUpperCase())) {
        setShowCustomArticleType(true)
        setCustomArticleType(shipment.article_type.toUpperCase())
      }

      if (shipment.has_parcel_image_1 && shipment.parcel_image_1_url) {
        setExistingImages((prev) => ({
          ...prev,
          img1: shipment.parcel_image_1_url,
        }))
      }

      if (shipment.has_parcel_image_2 && shipment.parcel_image_2_url) {
        setExistingImages((prev) => ({
          ...prev,
          img2: shipment.parcel_image_2_url,
        }))
      }

      const mediaList = shipment.images || shipment.media || []
      if (mediaList.length) {
        setExistingMedia(mediaList)
      } else {
        const legacy = []
        if (shipment.parcel_image_1_url) {
          legacy.push({ id: null, media_type: 'photo', cdn_url: shipment.parcel_image_1_url })
        }
        if (shipment.parcel_image_2_url) {
          legacy.push({ id: null, media_type: 'photo', cdn_url: shipment.parcel_image_2_url })
        }
        setExistingMedia(legacy)
      }
      setRemovedMediaIds([])
      setShipmentMediaFiles([])
    } catch (error) {
      console.error("Error fetching shipment:", error)
      Alert.error("Failed to load shipment")
      navigate("/shipments")
    } finally {
      setLoading(false)
    }

    fetchEwayBills()
  }

  const fetchLocationHubs = async () => {
    try {
      const response = await shipmentService.getAllLocationHubs()
      const allLocations = filterShipmentLocations(response.data.hubs || [])
      setConsignerLocations(allLocations)
      setConsigneeLocations(filterBranchLocations(allLocations))
    } catch (error) {
      console.error("Error fetching location hubs:", error)
    }
  }

  const calculateFreightCharges = () => {
    if (chargeSplitMode) {
      return Number(chargeSplit.freight || 0).toFixed(2)
    }
    if (isCargoMode) {
      const totalChargeable = sumChargeableWeight(parcels)
      return calcFreightFromRate(toNum(formData.rate_per_kg), totalChargeable).toFixed(2)
    }
    return calcBusFreight(parcels, formData.rate_per_kg, busFreightByWeight).toFixed(2)
  }

  // Live % extras (same as Add Shipment) so edit recalculates loading/unloading/freight.
  useEffect(() => {
    if (!chargeSplitMode) return
    setFormData((prev) => {
      let changed = false
      const next = { ...prev }
      EXTRA_SHIPMENT_CHARGE_KEYS.forEach((key) => {
        if (!chargeDefaults[key]?.enabled) return
        if (normalizeChargeMode(chargeDefaults[key].mode) !== 'percent') return
        if (key === 'loading' && !loadingAtBooking) return
        if (key === 'unloading' && !unloadingAtBooking) return
        const field = EXTRA_CHARGE_FORM_FIELDS[key]
        const val = String(chargeSplit.extras[key] ?? 0)
        if (next[field] !== val) {
          next[field] = val
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [chargeSplitMode, chargeSplit, chargeDefaults, loadingAtBooking, unloadingAtBooking])

  // Keep paid_amount in sync when status is paid and total changes (same as Add).
  useEffect(() => {
    if (formData.mode_of_payment !== 'paid') return
    const total = calculateTotalAmount()
    if (String(formData.paid_amount) === String(total)) return
    setFormData((prev) => (
      prev.mode_of_payment === 'paid' && String(prev.paid_amount) !== String(total)
        ? { ...prev, paid_amount: total }
        : prev
    ))
  }, [
    formData.mode_of_payment,
    formData.freight_charges,
    formData.handling_charges,
    formData.delivery_charges,
    formData.door_pickup_charges,
    formData.other_charges,
    formData.docket_fee,
    formData.discount,
    formData.sgst_percentage,
    formData.cgst_percentage,
    formData.igst_percentage,
    formData.no_of_articles,
    formData.article_amount,
    formData.rate_per_kg,
    formData.loading_charges,
    formData.unloading_charges,
    formData.toll_charges,
    formData.fuel_surcharge,
    formData.packing_charges,
    formData.insurance_charges,
    parcels,
    isCargoMode,
    chargeSplitMode,
    chargeSplit.freight,
    articleFreightBase,
  ])

  // Percent insurance: update when freight changes; keep loaded value on first run
  useEffect(() => {
    if (!chargeDefaults.insurance?.enabled) return
    if (chargeDefaults.insurance?.mode !== 'percent') return
    const freight = Number.parseFloat(calculateFreightCharges()) || 0
    if (insuranceFreightSyncedRef.current === null) {
      insuranceFreightSyncedRef.current = freight
      return
    }
    if (insuranceFreightSyncedRef.current === freight) return
    insuranceFreightSyncedRef.current = freight
    const next = String(calcInsuranceFromSettings(chargeDefaults.insurance, freight))
    setFormData((prev) => (
      prev.insurance_charges === next ? prev : { ...prev, insurance_charges: next }
    ))
  }, [
    chargeDefaults.insurance?.enabled,
    chargeDefaults.insurance?.mode,
    chargeDefaults.insurance?.amount,
    parcels,
    formData.rate_per_kg,
    isCargoMode,
    busFreightByWeight,
    chargeSplitMode,
    chargeSplit.freight,
  ])

  const sharedExtraChargesTotal = () => {
    let total = 0
    if ((loadingAtBooking && (chargeDefaults.loading.enabled || toNum(formData.loading_charges) > 0))) {
      total += resolveChargeAmount('loading', 'loading_charges')
    } else if (!loadingAtBooking && toNum(formData.loading_charges) > 0) {
      total += resolveChargeAmount('loading', 'loading_charges')
    }
    if ((unloadingAtBooking && (chargeDefaults.unloading?.enabled || toNum(formData.unloading_charges) > 0))
      || (!unloadingAtBooking && toNum(formData.unloading_charges) > 0)) {
      total += resolveChargeAmount('unloading', 'unloading_charges')
    }
    if (chargeDefaults.toll.enabled || toNum(formData.toll_charges) > 0) total += toNum(formData.toll_charges)
    if (chargeDefaults.fuel_surcharge.enabled || toNum(formData.fuel_surcharge) > 0) total += toNum(formData.fuel_surcharge)
    if (chargeDefaults.packing.enabled || toNum(formData.packing_charges) > 0) total += toNum(formData.packing_charges)
    return total
  }

  const roundRupee = (value) => Math.round(Number(value) || 0)
  const ceilRupee = (value) => {
    const paisa = Math.round((Number(value) || 0) * 100) / 100
    return Math.ceil(paisa - 1e-9)
  }

  const calculateTotalAmount = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = (chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0)
      ? (Number.parseFloat(formData.handling_charges) || 0)
      : 0
    const deliveryCharges = (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
      ? resolveChargeAmount('door_delivery', 'delivery_charges')
      : 0
    const doorPickupCharges = (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
      ? resolveChargeAmount('door_pickup', 'door_pickup_charges')
      : 0
    const docketFee = (chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0)
      ? (Number.parseFloat(formData.docket_fee) || 0)
      : 0
    const lateCharges = Number.parseFloat(formData.late_charges) || 0
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const insuranceCharges = chargeDefaults.insurance?.enabled
      ? (Number.parseFloat(formData.insurance_charges) || 0)
      : 0
    const discount = Number.parseFloat(formData.discount) || 0
    const sharedExtra = sharedExtraChargesTotal()

    const subtotal = Math.max(
      0,
      freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + lateCharges + otherCharges + insuranceCharges + sharedExtra - discount,
    )
    const bookingGst = deriveRcmGst()
    const gstBase = bookingGst.override ? Math.max(0, freightCharges) : subtotal

    let sgstPct = 0
    let cgstPct = 0
    let igstPct = 0
    let includeGstInTotal = true
    if (!gstExempt) {
      if (bookingGst.override) {
        sgstPct = Number(bookingGst.sgst) || 0
        cgstPct = Number(bookingGst.cgst) || 0
        igstPct = Number(bookingGst.igst) || 0
        includeGstInTotal = bookingGst.includeInTotal !== false
      } else {
        sgstPct = Number.parseFloat(formData.sgst_percentage) || 0
        cgstPct = Number.parseFloat(formData.cgst_percentage) || 0
        igstPct = Number.parseFloat(formData.igst_percentage) || 0
      }
    }

    const sgst = roundRupee((gstBase * sgstPct) / 100)
    const cgst = roundRupee((gstBase * cgstPct) / 100)
    const igst = roundRupee((gstBase * igstPct) / 100)

    const payable = subtotal + (includeGstInTotal ? sgst + cgst + igst : 0)
    return String(ceilRupee(payable))
  }

  const calculateGSTAmounts = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = (chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0)
      ? (Number.parseFloat(formData.handling_charges) || 0)
      : 0
    const deliveryCharges = (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
      ? resolveChargeAmount('door_delivery', 'delivery_charges')
      : 0
    const doorPickupCharges = (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
      ? resolveChargeAmount('door_pickup', 'door_pickup_charges')
      : 0
    const docketFee = (chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0)
      ? (Number.parseFloat(formData.docket_fee) || 0)
      : 0
    const lateCharges = Number.parseFloat(formData.late_charges) || 0
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const insuranceCharges = chargeDefaults.insurance?.enabled
      ? (Number.parseFloat(formData.insurance_charges) || 0)
      : 0
    const discount = Number.parseFloat(formData.discount) || 0
    const sharedExtra = sharedExtraChargesTotal()

    const subtotal = Math.max(
      0,
      freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + lateCharges + otherCharges + insuranceCharges + sharedExtra - discount,
    )
    const bookingGst = deriveRcmGst()
    const gstBase = bookingGst.override ? Math.max(0, freightCharges) : subtotal

    let sgstPct = 0
    let cgstPct = 0
    let igstPct = 0
    if (!gstExempt) {
      if (bookingGst.override) {
        sgstPct = Number(bookingGst.sgst) || 0
        cgstPct = Number(bookingGst.cgst) || 0
        igstPct = Number(bookingGst.igst) || 0
      } else {
        sgstPct = Number.parseFloat(formData.sgst_percentage) || 0
        cgstPct = Number.parseFloat(formData.cgst_percentage) || 0
        igstPct = Number.parseFloat(formData.igst_percentage) || 0
      }
    }

    return {
      sgst: String(roundRupee((gstBase * sgstPct) / 100)),
      cgst: String(roundRupee((gstBase * cgstPct) / 100)),
      igst: String(roundRupee((gstBase * igstPct) / 100)),
    }
  }

  const calculateBalanceAmount = () => {
    const totalAmount = Number.parseFloat(calculateTotalAmount())
    const paid = Number.parseFloat(formData.paid_amount) || 0
    return String(ceilRupee(totalAmount - paid))
  }

  const handleEnterAsTab = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const form = e.target.form
      if (!form) return
      const elements = Array.from(
        form.querySelectorAll(
          "input:not([type=hidden]):not([disabled]):not([type=checkbox]):not([type=radio]), select:not([disabled]), textarea:not([disabled])"
        )
      ).filter((el) => el.offsetParent !== null)
      const submitBtn =
        !isMobileLayout || mobileStepRef.current >= MOBILE_STEPS.length - 1
          ? document.getElementById("edit-shipment-submit") ||
            document.getElementById("edit-shipment-submit-mobile")
          : null
      if (submitBtn) elements.push(submitBtn)
      const index = elements.indexOf(e.target)
      if (index > -1 && index + 1 < elements.length) {
        elements[index + 1].focus()
      }
    }
  }

  const handleLocationDropdownKeyDown = (e, config) =>
    handleSuggestionDropdownKeyDown(e, { ...config, onEnterFallback: handleEnterAsTab })

  // Validate charge reduction for Agent role
  const validateAgentChargeReduction = (fieldName, newValue) => {
    if (!isAgentRole) return true

    const originalValue = Number.parseFloat(originalCharges[fieldName]) || 0
    const newVal = Number.parseFloat(newValue) || 0

    if (newVal < originalValue) {
      Alert.error(`As an Agent, you cannot reduce ${fieldName.replace(/_/g, ' ')}. You can only keep it the same or increase it.`)
      return false
    }
    return true
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target

    const chargeFields = [
      "freight_charges",
      "handling_charges",
      "delivery_charges",
      "door_pickup_charges",
      "late_charges",
      "docket_fee",
      "other_charges",
      "insurance_charges",
      "discount",
      "paid_amount",
      "article_amount",
      "rate_per_kg",
      "loading_charges",
      "unloading_charges",
      "toll_charges",
      "fuel_surcharge",
      "packing_charges",
      "sgst_percentage",
      "cgst_percentage",
      "igst_percentage",
    ]

    if (name === "article_type") {
      if (value === "OTHER") {
        setShowCustomArticleType(true)
        setCustomArticleType("")
        setFormData((prev) => ({ ...prev, article_type: "" }))
        return
      }
      setShowCustomArticleType(false)
      setFormData((prev) => ({ ...prev, article_type: value.toUpperCase() }))
      return
    }

    if (chargeFields.includes(name)) {
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
        // Validate Agent role restrictions on charge reduction
        const restrictedCharges = [
          "handling_charges",
          "delivery_charges",
          "door_pickup_charges",
          "late_charges",
          "docket_fee",
          "other_charges",
        ]

        if (restrictedCharges.includes(name) && !validateAgentChargeReduction(name, value)) {
          return
        }

        // Validate discount increase for Agent
        if (name === "discount" && !validateAgentChargeReduction(name, value)) {
          return
        }

        setFormData({ ...formData, [name]: value })
        if (validationRules[name]) validateField(name, value, validationRules[name])
      }
    } else {
      let filtered = value
      if (name === 'consigner_contact' || name === 'consignee_contact') filtered = inputFilters.digits10(value)
      if (name === 'consigner_gst' || name === 'consignee_gst') {
        filtered = inputFilters.uppercase(value).replace(/[^A-Z0-9]/g, '').slice(0, 15)
      }
      
      const updatedFormData = {
        ...formData,
        [name]: type === "number" ? (filtered === "" ? "" : Number.parseFloat(filtered)) : filtered,
      }

      if (name === "mode_of_payment") {
        if (value === "to_pay") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
          updatedFormData.shipment_client_id = ""
          setUseShipmentClient(false)
          setSelectedClient(null)
        } else if (value === "credit") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
          setUseShipmentClient(true)
        } else if (value === "paid") {
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = calculateTotalAmount()
          updatedFormData.shipment_client_id = ""
          setUseShipmentClient(false)
          setSelectedClient(null)
        } else if (value === "paid_balance") {
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = ""
        }
      }

      setFormData(updatedFormData)
      if (validationRules[name]) validateField(name, updatedFormData[name], validationRules[name])
    }
  }

  const handleImageChange = (e) => {
    const { name } = e.target
    const file = e.target.files?.[0]
    if (file) {
      setFormData({ ...formData, [name]: file })
      const reader = new FileReader()
      reader.onload = (e) => {
        if (name === "parcel_image_1") {
          setImagePreview({ ...imagePreview, img1: e.target.result })
        } else {
          setImagePreview({ ...imagePreview, img2: e.target.result })
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    if (!validate(formData, validationRules)) return
    try {
      setSaving(true)

      // Final validation for Agent role
      if (isAgentRole) {
        const restrictedCharges = {
          handling_charges: originalCharges.handling_charges,
          delivery_charges: originalCharges.delivery_charges,
          door_pickup_charges: originalCharges.door_pickup_charges,
          late_charges: originalCharges.late_charges,
          docket_fee: originalCharges.docket_fee,
          other_charges: originalCharges.other_charges,
          discount: originalCharges.discount,
        }

        for (const [field, originalValue] of Object.entries(restrictedCharges)) {
          const currentValue = Number.parseFloat(formData[field]) || 0
          const original = Number.parseFloat(originalValue) || 0
          if (currentValue < original) {
            Alert.error(`Agent role cannot reduce ${field.replace(/_/g, ' ')}`)
            setSaving(false)
            return
          }
        }
      }

      if (canAdminEditPaymentStatus && formData.mode_of_payment === "credit" && !formData.shipment_client_id) {
        Alert.error("Select a credit client before saving Credit")
        setSaving(false)
        return
      }

      if (!isCargoMode) {
        const hasArticleType = parcels.some((p) => (p.article_type || '').toString().trim())
        if (!hasArticleType) {
          Alert.error("Please add at least one article with a type")
          setSaving(false)
          return
        }
        if (busFreightByWeight) {
          if (sumBusArticleWeight(parcels) <= 0) {
            Alert.error("Please enter weight on articles for weight-based freight")
            setSaving(false)
            return
          }
        } else {
          const hasRate = parcels.some((p) => toNum(p.article_amount) > 0)
          if (!hasRate) {
            Alert.error("Please enter article rate for at least one article")
            setSaving(false)
            return
          }
        }
      }

      const busSync = isCargoMode ? null : syncFormFromBusArticles(parcels)
      const submitData = {
        ...formData,
        eway_bill_numbers: submittedEwayBills.length > 0 ? submittedEwayBills : undefined,
        created_at: formData.booking_date,
        shipment_type: isCargoMode ? "cargo" : "bus",
        no_of_articles: isCargoMode ? sumParcelQty(parcels) : busSync.no_of_articles,
        article_amount: isCargoMode ? 0 : (busFreightByWeight ? 0 : busSync.article_amount),
        article_type: isCargoMode ? (formData.article_type || "PALLET") : busSync.article_type,
        weight: isCargoMode
          ? sumChargeableWeight(parcels)
          : (busFreightByWeight ? sumBusArticleWeight(parcels) : formData.weight),
        freight_charges: Number.parseFloat(calculateFreightCharges()) || 0,
        handling_charges: (chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0)
          ? (Number.parseFloat(formData.handling_charges) || 0)
          : 0,
        delivery_charges: (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
          ? resolveChargeAmount('door_delivery', 'delivery_charges')
          : 0,
        door_pickup_charges: (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
          ? resolveChargeAmount('door_pickup', 'door_pickup_charges')
          : 0,
        late_charges: Number.parseFloat(formData.late_charges) || 0,
        docket_fee: (chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0)
          ? (Number.parseFloat(formData.docket_fee) || 0)
          : 0,
        other_charges: Number.parseFloat(formData.other_charges) || 0,
        insurance_charges: chargeDefaults.insurance?.enabled
          ? (Number.parseFloat(formData.insurance_charges) || 0)
          : 0,
        discount: Number.parseFloat(formData.discount) || 0,
        rate_per_kg: (isCargoMode || busFreightByWeight) ? toNum(formData.rate_per_kg) : 0,
        loading_charges: loadingAtBooking
          ? resolveChargeAmount('loading', 'loading_charges')
          : toNum(formData.loading_charges),
        unloading_charges: unloadingAtBooking
          ? resolveChargeAmount('unloading', 'unloading_charges')
          : toNum(formData.unloading_charges),
        toll_charges: toNum(formData.toll_charges),
        fuel_surcharge: toNum(formData.fuel_surcharge),
        packing_charges: toNum(formData.packing_charges),
        parcels: parcelsForApi(parcels),
        sgst_percentage: gstExempt
          ? 0
          : deriveRcmGst().override
          ? (Number(deriveRcmGst().sgst) || 0)
          : (Number.parseFloat(formData.sgst_percentage) || 0),
        cgst_percentage: gstExempt
          ? 0
          : deriveRcmGst().override
          ? (Number(deriveRcmGst().cgst) || 0)
          : (Number.parseFloat(formData.cgst_percentage) || 0),
        igst_percentage: gstExempt
          ? 0
          : deriveRcmGst().override
          ? (Number(deriveRcmGst().igst) || 0)
          : (Number.parseFloat(formData.igst_percentage) || 0),
        gst_exempt: gstExempt,
        paid_amount: Number.parseFloat(formData.paid_amount) || 0,
        shipment_client_id: formData.mode_of_payment === "credit" ? (formData.shipment_client_id || "") : "",
        payment_mode: formData.payment_mode || null,
        updated_by: user?.user_id,
        shipment_media: shipmentMediaFiles,
        remove_media_ids: removedMediaIds,
      }

      if (!ewaybillFeatureOn && submittedEwayBills.length > 0) {
        try {
          await shipmentService.addEwayBills(shipmentId, submittedEwayBills)
          Alert.success(`${submittedEwayBills.length} new E-way bill(s) added successfully`)
        } catch (ewayError) {
          console.error("Failed to add e-way bills:", ewayError)
          Alert.error("Shipment updated but failed to add E-way bills")
        }
      }
      await shipmentService.updateShipment(shipmentId, submitData)
      Alert.success("Shipment updated successfully")
      navigate("/shipments")
    } catch (error) {
      console.error("Error updating shipment:", error)
      Alert.error(error.response?.data?.error || "Failed to update shipment")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout fullWidth fillViewport>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading shipment..." />
        </div>
      </DashboardLayout>
    )
  }

  const isConsignorSuggestionsOpen =
    !isMobileLayout &&
    ((useShipmentClient && showClientDropdown && filteredClients.length > 0) ||
      (showConsignorSuggestions && (consignorSuggestions.length > 0 || consignorSuggestionsLoading)))

  const isConsigneeSuggestionsOpen =
    !isMobileLayout &&
    showConsigneeSuggestions &&
    (consigneeSuggestions.length > 0 || consigneeSuggestionsLoading)

  return (
    <DashboardLayout fullWidth fillViewport>
      <form
        id="edit-shipment-form"
        noValidate={isMobileLayout}
        onSubmit={handleFormSubmit}
        className={`min-w-0 flex flex-col flex-1 min-h-0 md:[&_input]:py-1.5 md:[&_select]:py-1.5 md:[&_textarea]:py-1.5 ${isMobileLayout ? (mobileStep === 4 ? "overflow-y-auto pb-20" : "overflow-hidden pb-20") : "md:overflow-hidden"}`}
      >
        {isMobileLayout && (
          <div className="sticky top-0 z-30 bg-white shrink-0 mb-3 pb-2 border-b border-gray-100">
            <div className="flex items-center gap-1 mb-2">
              {MOBILE_STEPS.map((step, idx) => (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handleMobileStepTabClick(idx)}
                  className={`flex-1 min-w-0 text-center py-1.5 px-0.5 rounded-lg text-[10px] font-semibold transition truncate ${
                    idx === mobileStep
                      ? "bg-orange-500 text-white shadow-sm"
                      : idx < mobileStep
                        ? "bg-orange-100 text-orange-700"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {step.label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <h1 className="flex items-center gap-1.5 text-sm font-bold text-gray-700 truncate">
                <MapPin className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                <span className="truncate">Edit {shipmentId}</span>
              </h1>
              <Button variant="secondary" type="button" size="sm" onClick={() => navigate("/shipments")}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="hidden md:block mb-2 space-y-2 shrink-0 z-30 bg-gray-50/95 pb-2 border-b border-gray-200/80">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="flex flex-wrap items-center gap-2 text-base sm:text-xl font-bold text-gray-700">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-buttonhoverTxt shrink-0" />
                <span className="truncate">Edit Shipment {shipmentId}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                  shipmentIdType === "manual"
                    ? "bg-orange-100 text-orange-700 border border-orange-300"
                    : "bg-green-100 text-green-700 border border-green-300"
                }`}>
                  {shipmentIdType === "manual" ? "Manual ID" : "Auto ID"}
                </span>
              </h1>
              {isAgentRole && (
                <p className="text-xs sm:text-sm text-amber-700 font-semibold mt-1">
                  Agent Mode: You can only increase charges, not decrease them
                </p>
              )}
            </div>
            <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3 shrink-0">
              <Button variant="secondary" type="button" onClick={() => navigate("/shipments")}>
                Cancel
              </Button>
              <Button variant="secondary" icon={ArrowLeft} type="button" onClick={() => navigate("/shipments")} className="hidden sm:inline-flex">
                Back to Dashboard
              </Button>
              <Button id="edit-shipment-submit" variant="primary" type="button" onClick={handleFormSubmit} loading={saving} disabled={saving} className="hidden md:inline-flex">
                {saving ? "Updating..." : "Update Shipment"}
              </Button>
            </div>
          </div>
        </div>

        <div className={isMobileLayout ? "contents" : "md:flex-1 md:min-h-0 md:overflow-y-auto md:overflow-x-hidden md:min-w-0 md:pb-2"}>

        <div className={`${mobileStepPanelClass(0)} md:!block md:shrink-0 md:mb-2 md:space-y-2`}>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-4 sm:py-2 mb-2">
            <div className="flex flex-col gap-2 sm:gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-xs sm:text-sm font-semibold text-gray-700">GC Number</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="text-sm sm:text-base font-bold text-blue-700">{shipmentId}</span>
                {consignmentNo && (
                  <span className="text-xs text-gray-600 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                    CN: {consignmentNo}
                  </span>
                )}
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                  shipmentIdType === "manual"
                    ? "bg-orange-100 text-orange-700 border border-orange-300"
                    : "bg-green-100 text-green-700 border border-green-300"
                }`}>
                  {shipmentIdType === "manual" ? "Manual ID" : "Auto ID"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 mb-2 min-w-0 shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 pb-2 border-b-2 border-gray-200">
              <h2 className="text-sm sm:text-base font-semibold text-gray-700">Destination & Documents</h2>
              <div className="flex flex-row gap-2 shrink-0 flex-wrap">
                {!ewaybillFeatureOn && (
                  <Button variant="secondary" type="button" tabIndex={-1} onClick={() => setShowEwayBillModal(true)}>
                    Add E-way Unique ID
                  </Button>
                )}
                <Button
                  variant={(existingEwayBills.length + submittedEwayBills.length) > 0 ? "primary" : "secondary"}
                  type="button"
                  tabIndex={-1}
                  onClick={handleViewEwayBills}
                >
                  View Eway Bill {(existingEwayBills.length + submittedEwayBills.length) > 0 && `(${existingEwayBills.length + submittedEwayBills.length})`}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-3">
              <div className="flex flex-col relative">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  From Destination <span className="text-red-500">*</span>
                </label>
                <div className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                  {formData.consigner_location ? (
                    <span>{formData.consigner_location}</span>
                  ) : (
                    <span className="text-gray-400">From branch</span>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-gray-400">From branch cannot be changed after booking.</p>
              </div>

              {canEditShipmentStatus && (
                <div className="flex flex-col">
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                    Status
                  </label>
                  {isCreditClientShipment ? (
                    <>
                      <div className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed">
                        {formData.status || "Booked"}
                      </div>
                      <p className="mt-1 text-[11px] text-gray-400">Status cannot be changed for a credit client shipment.</p>
                    </>
                  ) : (
                    <SelectField
                      variant="form"
                      name="status"
                      value={formData.status || "Booked"}
                      onChange={handleChange}
                      onKeyDown={handleEnterAsTab}
                      className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                    >
                      {editStatusOptions.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </SelectField>
                  )}
                </div>
              )}

              <div className="flex flex-col relative" ref={consigneeRef}>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  To Destination <span className="text-red-500">*</span>
                </label>
                <div
                  onClick={() => { setShowConsigneeDropdown(true); setConsigneeHighlightIndex(-1); }}
                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-xl cursor-pointer select-none ${
                    formData.consignee_location ? "border-gray-300 bg-white hover:border-orange-400" : "border-red-300 bg-white hover:border-red-400"
                  }`}
                >
                  {formData.consignee_location ? (
                    <span className="text-gray-800">{formData.consignee_location}</span>
                  ) : (
                    <span className="text-gray-400">select location</span>
                  )}
                </div>
                {showConsigneeDropdown && (
                  <div className="absolute z-50 w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                      <input
                        type="text"
                        autoFocus
                        value={consigneeLocationSearch}
                        onChange={(e) => { setConsigneeLocationSearch(e.target.value); setConsigneeHighlightIndex(-1); }}
                        onKeyDown={(e) =>
                          handleLocationDropdownKeyDown(e, {
                            isOpen: showConsigneeDropdown,
                            options: filteredConsigneeLocations,
                            highlightIndex: consigneeHighlightIndex,
                            setHighlightIndex: setConsigneeHighlightIndex,
                            onClose: () => { setShowConsigneeDropdown(false); setConsigneeLocationSearch(""); },
                            onSelect: (loc) => {
                              const label = formatHubBranchLabel(loc)
                              if ((loc?.id ?? "") === formData.sender_location_hub_id && formData.sender_location_hub_id !== "") {
                                Alert.error("Sender and Receiver locations cannot be the same")
                                return
                              }
                              const hubRate = rateFromHub(loc)
                              setFormData({
                                ...formData,
                                consignee_location: label,
                                receiver_location_hub_id: loc?.id ?? "",
                                ...(hubRate !== null ? { rate_per_kg: hubRate } : {}),
                              })
                              setConsigneeLocationSearch("")
                              setShowConsigneeDropdown(false)
                              setConsigneeHighlightIndex(-1)
                            },
                          })
                        }
                        placeholder="Type to search..."
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {filteredConsigneeLocations.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">No locations found</div>
                      ) : filteredConsigneeLocations.map((loc, idx) => (
                        <div
                          key={loc.hub_id}
                          onMouseEnter={() => setConsigneeHighlightIndex(idx)}
                          onClick={() => {
                            const label = formatHubBranchLabel(loc)
                            if ((loc?.id ?? "") === formData.sender_location_hub_id && formData.sender_location_hub_id !== "") {
                              Alert.error("Sender and Receiver locations cannot be the same")
                              return
                            }
                            const hubRate = rateFromHub(loc)
                            setFormData({
                              ...formData,
                              consignee_location: label,
                              receiver_location_hub_id: loc?.id ?? "",
                              ...(hubRate !== null ? { rate_per_kg: hubRate } : {}),
                            })
                            setConsigneeLocationSearch("")
                            setShowConsigneeDropdown(false)
                            setConsigneeHighlightIndex(-1)
                          }}
                          className={`px-3 sm:px-4 py-2 cursor-pointer text-sm sm:text-base border-b border-gray-100 last:border-b-0 ${
                            idx === consigneeHighlightIndex ? "bg-orange-50" : "hover:bg-orange-50"
                          }`}
                        >
                          {formatHubBranchLabel(loc)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Booking Date</label>
                <input type="date" name="booking_date" value={formData.booking_date} onChange={handleChange} onKeyDown={handleEnterAsTab} max={getLocalDateString()} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
              </div>
              <div className="flex flex-col min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Invoice Number</label>
                <input
                  type="text"
                  name="invoice_number"
                  placeholder="Optional"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  onKeyDown={handleEnterAsTab}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                />
              </div>
              <div className="flex flex-col min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Invoice Value</label>
                <input
                  type="text"
                  name="shipment_value"
                  placeholder="Optional"
                  value={formData.shipment_value}
                  onChange={handleChange}
                  onKeyDown={handleEnterAsTab}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                />
              </div>
            </div>

            {(loadingEwayBills || existingEwayBills.length > 0 || submittedEwayBills.length > 0) && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                {loadingEwayBills ? (
                  <p className="text-xs text-gray-500">Loading E-way bills...</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {existingEwayBills.map((bill) => (
                      <div key={bill.id} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                        <span className="text-xs font-mono text-gray-800">{bill.eway_bill_number}</span>
                        {bill.status === 'cancelled' && (
                          <span className="text-[10px] text-red-600 font-medium">Cancelled</span>
                        )}
                        {ewaybillFeatureOn && bill.status !== 'cancelled' ? (
                          <>
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => {
                                setSelectedEwayBill(bill)
                                setUpdateVehicleForm({
                                  vehicleNo: bill.vehicle_no || formData.vehicle_number || '',
                                  reasonRem: 'Vehicle Update',
                                })
                                setShowUpdateVehicleModal(true)
                              }}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                              Vehicle
                            </button>
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => handleCancelEwayBill(bill)}
                              className="text-red-600 hover:text-red-800 text-xs font-medium"
                            >
                              Cancel
                            </button>
                          </>
                        ) : !ewaybillFeatureOn ? (
                          <button type="button" tabIndex={-1} onClick={() => handleDeleteEwayBill(bill.id, bill.eway_bill_number)} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                        ) : null}
                      </div>
                    ))}
                    {!ewaybillFeatureOn && submittedEwayBills.map((bill, idx) => (
                      <div key={`new-${idx}`} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                        <span className="text-xs font-mono text-gray-800">{bill}</span>
                        <button type="button" tabIndex={-1} onClick={() => { setSubmittedEwayBills(submittedEwayBills.filter((_, i) => i !== idx)); Alert.success("E-way bill removed from queue"); }} className="text-red-600 hover:text-red-800 text-xs font-medium">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={`space-y-3 md:space-y-3 min-w-0 flex-1 min-h-0 md:overflow-hidden ${isMobileLayout && mobileStep === 0 ? "hidden" : ""} ${isMobileLayout ? (mobileStep === 4 ? "flex flex-col overflow-visible min-h-0 flex-1" : "flex flex-col overflow-hidden min-h-0 flex-1") : ""}`}>
          {/* Consignor (left) | Consignee (right) — stacked on mobile wizard */}
          <div className={`${isMobileLayout ? "contents" : `grid grid-cols-1 md:grid-cols-2 gap-3 relative ${isConsigneeSuggestionsOpen ? "z-[60]" : ""}`}`}>
              <div className={`rounded-xl pb-2 sm:pb-3 bg-white p-4 md:p-4 ${mobileStepPanelClass(1)} md:!block md:overflow-visible ${isConsignorSuggestionsOpen ? "relative z-50" : ""}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                  <h2 className="text-base sm:text-md font-semibold text-gray-700">Consignor Information</h2>
                  <div className="flex items-center gap-3 shrink-0">
                    {creditAllowed && (
                    <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" id="useShipmentClient" checked={useShipmentClient} onChange={(e) => handleUseClientChange(e.target.checked)} className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded" />
                      Select from Credit Clients
                    </label>
                    )}
                    <button type="button" tabIndex={-1} onClick={() => setShowConsignorExtra(!showConsignorExtra)} className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition">
                      {showConsignorExtra ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-3 mb-3">
                    <div className="flex flex-col relative">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="consigner_contact"
                        placeholder="10 digit mobile number"
                        value={formData.consigner_contact}
                        onChange={(e) => {
                          handleChange(e)
                          if (!useShipmentClient) {
                            setConsignorSuggestionHighlightIndex(-1)
                            fetchConsignorSuggestions(e.target.value)
                          }
                        }}
                        onFocus={() => {
                          if (!useShipmentClient && String(formData.consigner_contact || "").replace(/\D/g, "").length >= 10) {
                            setShowConsignorSuggestions(true)
                            setConsignorSuggestionHighlightIndex(-1)
                          }
                        }}
                        onBlur={() => setTimeout(() => { setShowConsignorSuggestions(false); setConsignorSuggestionHighlightIndex(-1) }, 200)}
                        onKeyDown={(e) =>
                          handleSuggestionDropdownKeyDown(e, {
                            isOpen: showConsignorSuggestions && !consignorSuggestionsLoading && consignorSuggestions.length > 0,
                            options: consignorSuggestions,
                            highlightIndex: consignorSuggestionHighlightIndex,
                            setHighlightIndex: setConsignorSuggestionHighlightIndex,
                            onSelect: handleConsignorSuggestionSelect,
                            onClose: () => { setShowConsignorSuggestions(false); setConsignorSuggestionHighlightIndex(-1) },
                            onEnterFallback: handleEnterAsTab,
                          })
                        }
                        required
                        autoComplete="off"
                        disabled={useShipmentClient}
                        maxLength={10}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consigner_contact ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`}
                      />
                      <ValidationMessage error={errors.consigner_contact} />
                      {showConsignorSuggestions && (consignorSuggestions.length > 0 || consignorSuggestionsLoading) && !useShipmentClient && (
                        <div className={getSuggestionListClass(isMobileLayout)}>
                          {consignorSuggestionsLoading && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
                          )}
                          {!consignorSuggestionsLoading && consignorSuggestions.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">No suggestions found</div>
                          )}
                          {Array.isArray(consignorSuggestions) && consignorSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              ref={(node) => { if (index === consignorSuggestionHighlightIndex) scrollHighlightedSuggestionIntoView(node) }}
                              onMouseEnter={() => setConsignorSuggestionHighlightIndex(index)}
                              onClick={() => handleConsignorSuggestionSelect(suggestion)}
                              className={getSuggestionItemClass(index === consignorSuggestionHighlightIndex)}
                            >
                              <div className="font-medium text-gray-800">{suggestion.name}</div>
                              <div className="text-xs text-gray-500">{suggestion.contact}</div>
                              {suggestion.address && <div className="text-xs text-gray-400 truncate">{suggestion.address}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Consignor Name <span className="text-red-500">*</span></label>
                      {useShipmentClient ? (
                        <>
                          <input
                            type="text"
                            value={selectedClient ? `${selectedClient.full_name} (${selectedClient.company_name})` : clientSearch}
                            onChange={(e) => { setClientSearch(e.target.value); setSelectedClient(null); setShowClientDropdown(true); setClientHighlightIndex(-1); }}
                            onFocus={() => { setShowClientDropdown(true); setClientHighlightIndex(-1); }}
                            onBlur={() => setTimeout(() => { setShowClientDropdown(false); setClientHighlightIndex(-1) }, 200)}
                            onKeyDown={(e) =>
                              handleSuggestionDropdownKeyDown(e, {
                                isOpen: showClientDropdown,
                                options: filteredClients,
                                highlightIndex: clientHighlightIndex,
                                setHighlightIndex: setClientHighlightIndex,
                                onSelect: handleClientSelect,
                                onClose: () => { setShowClientDropdown(false); setClientHighlightIndex(-1) },
                                onEnterFallback: handleEnterAsTab,
                              })
                            }
                            className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                            required
                          />
                          {showClientDropdown && filteredClients.length > 0 && (
                            <div className={getSuggestionListClass(isMobileLayout)}>
                              {filteredClients.map((client, index) => (
                                <div
                                  key={index}
                                  ref={(node) => { if (index === clientHighlightIndex) scrollHighlightedSuggestionIntoView(node) }}
                                  onMouseEnter={() => setClientHighlightIndex(index)}
                                  onClick={() => handleClientSelect(client)}
                                  className={getSuggestionItemClass(index === clientHighlightIndex)}
                                >
                                  <div className="font-medium text-gray-800">{client.full_name}</div>
                                  <div className="text-xs text-gray-500">{client.contact}</div>
                                  {client.address && <div className="text-xs text-gray-400 truncate">{client.address}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            name="consigner_name"
                            placeholder="Consignor name"
                            value={formData.consigner_name}
                            onChange={handleChange}
                            onKeyDown={handleEnterAsTab}
                            required
                            autoComplete="off"
                            className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consigner_name ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                          />
                          <ValidationMessage error={errors.consigner_name} />
                        </>
                      )}
                    </div>
                  </div>
                  {showConsignorExtra && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 border p-4">
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Address</label>
                        <input type="text" name="consigner_address" value={formData.consigner_address} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={useShipmentClient && selectedClient} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100" />
                      </div>
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email</label>
                        <input type="email" name="consigner_email" value={formData.consigner_email} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={useShipmentClient && selectedClient} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consigner_email ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`} />
                        <ValidationMessage error={errors.consigner_email} />
                      </div>
                      <GstVerificationField
                        name="consigner_gst"
                        value={formData.consigner_gst}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        error={errors.consigner_gst}
                        enabled={gstVerificationOn}
                        disabled={useShipmentClient && selectedClient}
                        onLookup={onSenderGstLookup}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className={`rounded-xl pb-2 sm:pb-3 bg-white p-4 md:p-4 ${mobileStepPanelClass(2)} md:!block md:overflow-visible ${isConsigneeSuggestionsOpen ? "relative z-[70]" : ""}`}>
                <div className="flex items-center justify-between mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                  <h2 className="text-base sm:text-md font-semibold text-gray-700">Consignee Information</h2>
                  {creditAllowed && (
                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.credit_client_side === "receiver"}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            credit_client_side: e.target.checked ? "receiver" : (prev.shipment_client_id ? "sender" : ""),
                            mode_of_payment: prev.mode_of_payment,
                          }))
                        }}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded"
                      />
                      Credit client is the receiver
                    </label>
                  )}
                  <button type="button" tabIndex={-1} onClick={() => setShowConsigneeExtra(!showConsigneeExtra)} className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition">
                    {showConsigneeExtra ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
                <div className="space-y-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-3 mb-3">
                    <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="consignee_contact"
                        placeholder="10 digit mobile number"
                        value={formData.consignee_contact}
                        onChange={(e) => {
                          handleChange(e)
                          setConsigneeSuggestionHighlightIndex(-1)
                          fetchConsigneeSuggestions(e.target.value)
                        }}
                        onFocus={() => {
                          if (String(formData.consignee_contact || "").replace(/\D/g, "").length >= 10) {
                            setShowConsigneeSuggestions(true)
                            setConsigneeSuggestionHighlightIndex(-1)
                          }
                        }}
                        onBlur={() => setTimeout(() => { setShowConsigneeSuggestions(false); setConsigneeSuggestionHighlightIndex(-1) }, 200)}
                        onKeyDown={(e) =>
                          handleSuggestionDropdownKeyDown(e, {
                            isOpen: showConsigneeSuggestions && !consigneeSuggestionsLoading && consigneeSuggestions.length > 0,
                            options: consigneeSuggestions,
                            highlightIndex: consigneeSuggestionHighlightIndex,
                            setHighlightIndex: setConsigneeSuggestionHighlightIndex,
                            onSelect: handleConsigneeSuggestionSelect,
                            onClose: () => { setShowConsigneeSuggestions(false); setConsigneeSuggestionHighlightIndex(-1) },
                            onEnterFallback: handleEnterAsTab,
                          })
                        }
                        required
                        autoComplete="off"
                        maxLength={10}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consignee_contact ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                      />
                      <ValidationMessage error={errors.consignee_contact} />
                      {showConsigneeSuggestions && (consigneeSuggestions.length > 0 || consigneeSuggestionsLoading) && (
                        <div className={getSuggestionListClass(isMobileLayout)}>
                          {consigneeSuggestionsLoading && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
                          )}
                          {!consigneeSuggestionsLoading && consigneeSuggestions.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">No suggestions found</div>
                          )}
                          {Array.isArray(consigneeSuggestions) && consigneeSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              ref={(node) => { if (index === consigneeSuggestionHighlightIndex) scrollHighlightedSuggestionIntoView(node) }}
                              onMouseEnter={() => setConsigneeSuggestionHighlightIndex(index)}
                              onClick={() => handleConsigneeSuggestionSelect(suggestion)}
                              className={getSuggestionItemClass(index === consigneeSuggestionHighlightIndex)}
                            >
                              <div className="font-medium text-gray-800">{suggestion.name}</div>
                              <div className="text-xs text-gray-500">{suggestion.contact}</div>
                              {suggestion.address && <div className="text-xs text-gray-400 truncate">{suggestion.address}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Consignee Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="consignee_name"
                        placeholder="Consignee name"
                        value={formData.consignee_name}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        required
                        autoComplete="off"
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consignee_name ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                      />
                      <ValidationMessage error={errors.consignee_name} />
                    </div>
                  </div>
                  {showConsigneeExtra && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 p-4 border">
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Address</label>
                        <input type="text" name="consignee_address" placeholder="Full Address" value={formData.consignee_address} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                      </div>
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email</label>
                        <input type="email" name="consignee_email" value={formData.consignee_email} onChange={handleChange} onKeyDown={handleEnterAsTab} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consignee_email ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`} />
                        <ValidationMessage error={errors.consignee_email} />
                      </div>
                      <GstVerificationField
                        name="consignee_gst"
                        value={formData.consignee_gst}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        error={errors.consignee_gst}
                        enabled={gstVerificationOn}
                        onLookup={onReceiverGstLookup}
                      />
                    </div>
                  )}
                </div>
              </div>
          </div>

          {/* Shipment Details (left) | Shipment Charges (right) — stacked on mobile wizard */}
          <div className={`${isMobileLayout ? "contents" : "grid grid-cols-1 md:grid-cols-2 gap-3 md:min-h-0"}`}>
            <div className={`bg-white rounded-xl p-3 sm:p-4 mb-2 md:overflow-y-auto md:min-h-0 ${mobileStepPanelClass(3)} md:!block`}>
                <div className="flex items-center justify-between gap-2 mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                  <h2 className="text-base sm:text-lsm font-semibold text-gray-700 min-w-0">
                    Shipment Details
                  </h2>
                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowParcelImages(!showParcelImages)}
                      className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition"
                      aria-label="Toggle parcel images"
                    >
                      {showParcelImages ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-0">
                  {isCargoMode ? (
                    <PackageDimensionSummary
                      parcels={parcels}
                      mode="edit"
                      onAdd={() => {
                        setAddPackageAppendMode(false)
                        setShowAddPackageModal(true)
                      }}
                      onEdit={() => setShowEditPackageModal(true)}
                      onView={() => setShowViewPackageModal(true)}
                      onAddMore={() => {
                        setAddPackageAppendMode(true)
                        setShowAddPackageModal(true)
                      }}
                    />
                  ) : (
                    <BusArticlePackagesForm
                      articles={parcels}
                      onChange={handleBusArticlesChange}
                      freightByWeight={busFreightByWeight}
                      title="Article Details"
                      badge="Bus"
                    />
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3">
                        Photos &amp; Videos
                      </h3>
                      <ShipmentMediaUploader
                        files={shipmentMediaFiles}
                        onFilesChange={setShipmentMediaFiles}
                        existing={existingMedia}
                        removedIds={removedMediaIds}
                        onRemovedIdsChange={setRemovedMediaIds}
                        compact
                        dropzone={isMobileLayout}
                      />
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3">
                        Remarks / Parcel Details
                      </label>
                      <textarea
                        name="parcel_detail"
                        value={formData.parcel_detail}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        rows="4"
                        placeholder="Notes, special instructions, parcel description..."
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 min-h-[120px]"
                      />
                    </div>
                  </div>

                  {showParcelImages && !isCargoMode && !busFreightByWeight && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 border p-4 mb-4">
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Weight (kg)</label>
                        <input type="text" name="weight" value={formData.weight} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                      </div>
                    </div>
                  )}
                </div>
            </div>

            <div className={`bg-white rounded-xl p-3 sm:p-4 mb-2 md:overflow-y-auto md:min-h-0 overflow-visible ${isMobileLayout ? (mobileStep === 4 ? "block w-full min-w-0 overflow-visible pb-28" : "hidden") : ""} md:!block`}>
                <div className="flex items-center justify-between mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                  <h2 className="text-base sm:text-lsm font-semibold text-gray-700">Shipment Charges</h2>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {gstExempt && (
                      <span className="px-2 py-1 text-[11px] sm:text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded">
                        GST exempt (vegetable / flower / fruit / household item)
                      </span>
                    )}
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowGSTDetails(!showGSTDetails)}
                      disabled={gstExempt}
                      className={`flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded transition ${
                        gstExempt
                          ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
                          : 'text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-200'
                      }`}
                    >
                      GST
                      {showGSTDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {showGSTDetails && !gstExempt && (
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 p-3 sm:p-4 border border-purple-200 rounded-xl bg-purple-50/40 mb-3">
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">SGST %</label>
                      <input type="text" name="sgst_percentage" value={formData.sgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={rcmActive || gstExempt} readOnly={rcmActive || gstExempt} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || gstExempt ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`} />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CGST %</label>
                      <input type="text" name="cgst_percentage" value={formData.cgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={rcmActive || gstExempt} readOnly={rcmActive || gstExempt} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || gstExempt ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`} />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">IGST %</label>
                      <input type="text" name="igst_percentage" value={formData.igst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={rcmActive || gstExempt} readOnly={rcmActive || gstExempt} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || gstExempt ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`} />
                    </div>
                    <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().sgst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}><p className="text-xs text-gray-600 font-semibold">SGST</p><p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().sgst}</p></div>
                    <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().cgst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}><p className="text-xs text-gray-600 font-semibold">CGST</p><p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().cgst}</p></div>
                    <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().igst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}><p className="text-xs text-gray-600 font-semibold">IGST</p><p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().igst}</p></div>
                  </div>
                )}

                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 p-3 sm:p-4 border border-blue-200 rounded-xl bg-blue-50/40 mb-3">
                    {(chargeDefaults.insurance?.enabled || toNum(formData.insurance_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Insurance Charge
                          {chargeDefaults.insurance?.mode === 'percent' && (
                            <span className="text-gray-400 font-normal">
                              {` (${chargeDefaults.insurance.amount || 0}%)`}
                            </span>
                          )}
                        </label>
                        <input type="text" name="insurance_charges" value={formData.insurance_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {loadingAtBooking && (chargeDefaults.loading.enabled || toNum(formData.loading_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          {normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent' ? 'Loading' : 'Loading (per parcel)'}
                          {normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent' && (
                            <span className="text-gray-400 font-normal">
                              {` (${chargeDefaults.loading.amount || 0}%)`}
                            </span>
                          )}
                          {normalizeChargeMode(chargeDefaults.loading?.mode) !== 'percent' && parcelCountForCharges > 0 && (
                            <span className="block text-[10px] sm:text-xs text-gray-400 font-normal mt-0.5">
                              {`₹${formData.loading_charges || 0} × ${parcelCountForCharges} = ₹${perParcelChargeTotal(formData.loading_charges, parcelCountForCharges)}`}
                            </span>
                          )}
                        </label>
                        <input type="text" name="loading_charges" value={formData.loading_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent'} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent' ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-white'}`} />
                      </div>
                    )}
                    {unloadingAtBooking && (chargeDefaults.unloading?.enabled || toNum(formData.unloading_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Unloading
                          {normalizeChargeMode(chargeDefaults.unloading?.mode) === 'percent' && (
                            <span className="text-gray-400 font-normal">
                              {` (${chargeDefaults.unloading.amount || 0}%)`}
                            </span>
                          )}
                        </label>
                        <input type="text" name="unloading_charges" value={formData.unloading_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={normalizeChargeMode(chargeDefaults.unloading?.mode) === 'percent'} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${normalizeChargeMode(chargeDefaults.unloading?.mode) === 'percent' ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-white'}`} />
                      </div>
                    )}
                    {(chargeDefaults.toll.enabled || toNum(formData.toll_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Toll</label>
                        <input type="text" name="toll_charges" value={formData.toll_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.fuel_surcharge.enabled || toNum(formData.fuel_surcharge) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Fuel Surcharge</label>
                        <input type="text" name="fuel_surcharge" value={formData.fuel_surcharge} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.packing.enabled || toNum(formData.packing_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Packing</label>
                        <input type="text" name="packing_charges" value={formData.packing_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Handling Charges</label>
                        <input type="text" name="handling_charges" value={formData.handling_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Docket Fee</label>
                        <input type="text" name="docket_fee" value={formData.docket_fee} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Late Charges</label>
                      <input type="text" name="late_charges" value={formData.late_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                    </div>
                    {(chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Door Pickup
                        </label>
                        <input type="text" name="door_pickup_charges" value={formData.door_pickup_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Door Delivery
                        </label>
                        <input type="text" name="delivery_charges" value={formData.delivery_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Other Charges</label>
                      <input type="text" name="other_charges" value={formData.other_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                    </div>
                  </div>

                <div className="space-y-0">
                  {/* Primary charges — Freight, Rate/KG, Discount */}
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-3 mb-3">
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Freight Charges
                        {chargeSplitMode && (
                          <span className="text-gray-400 font-normal">
                            {` (${chargeSplit.remainderPercent || 0}%)`}
                          </span>
                        )}
                      </label>
                      <input type="text" value={calculateFreightCharges()} onKeyDown={handleEnterAsTab} disabled className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-600 font-semibold cursor-not-allowed" />
                    </div>
                    {(isCargoMode || busFreightByWeight || toNum(formData.rate_per_kg) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Rate / KG</label>
                        <input type="text" name="rate_per_kg" value={formData.rate_per_kg} onChange={handleChange} onKeyDown={handleEnterAsTab} placeholder="From receiver hub (optional)" className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Discount</label>
                      <input type="text" name="discount" value={formData.discount} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                  <div className="flex flex-col">
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Total Amount</label>
                    <div className="px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 font-semibold text-green-600">₹{calculateTotalAmount()}</div>
                    {rcmActive && deriveRcmGst().includeInTotal === false && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        GST shown above is under RCM and is not added to this total.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Payment Status</label>
                    <SelectField variant="form" dropUp name="mode_of_payment" disabled={!canAdminEditPaymentStatus} value={formData.mode_of_payment} onChange={handleChange} onKeyDown={handleEnterAsTab} className={`px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl ${canAdminEditPaymentStatus ? "bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" : "bg-gray-100 text-gray-600 cursor-not-allowed"}`}>
                      {editPaymentStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </SelectField>
                    <p className="mt-1.5 text-[11px] text-gray-500">
                      {canAdminEditPaymentStatus
                        ? "Admin can switch among To Pay, Paid, and Credit. Credit needs a credit client."
                        : "Payment mode stays as it was booked."}
                    </p>
                  </div>
                </div>

                {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                  <div ref={paidPaymentFieldsRef} className="space-y-4 mt-4 pb-2 overflow-visible">
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Payment Mode</label>
                        <SelectField variant="form" dropUp name="payment_mode" value={formData.payment_mode} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200">
                          <option value="">Select Mode</option>
                          {formData.payment_mode
                            && !SHIPMENT_PAYMENT_MODES.some((mode) => mode.value === formData.payment_mode) && (
                              <option value={formData.payment_mode}>
                                {formData.payment_mode.replace(/_/g, " ")}
                              </option>
                            )}
                          {SHIPMENT_PAYMENT_MODES.map((mode) => (
                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                          ))}
                        </SelectField>
                      </div>
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Paid Amount</label>
                        <input type="text" name="paid_amount" placeholder="0.00" value={formData.paid_amount} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl">
                        <p className="text-xs text-gray-600 font-semibold">Paid Amount</p>
                        <p className="text-lg font-bold text-blue-600">₹{formData.paid_amount || "0.00"}</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-xl">
                        <p className="text-xs text-gray-600 font-semibold">Balance Due</p>
                        <p className="text-lg font-bold text-blue-600">₹{calculateBalanceAmount()}</p>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>

        </div>
        </div>
      </form>

      {isMobileLayout && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
          {mobileStep > 0 ? (
            <Button variant="secondary" type="button" icon={ChevronLeft} onClick={goToPrevMobileStep}>Back</Button>
          ) : (
            <div className="w-16" />
          )}
          <Button
            variant="primary"
            type="button"
            icon={mobileStep < MOBILE_STEPS.length - 1 ? ChevronRight : undefined}
            iconPosition="right"
            onClick={handleMobilePrimaryAction}
            loading={saving && mobileStep >= MOBILE_STEPS.length - 1}
            disabled={saving}
          >
            {mobileStep < MOBILE_STEPS.length - 1
              ? "Next"
              : saving
                ? "Updating..."
                : "Update Shipment"}
          </Button>
        </div>
      )}

      {showEwayBillModal && !ewaybillFeatureOn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-md">
            <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Add E-WayBill</h3>
              <Button variant="ghost" onClick={handleCloseEwayBillModal} className="text-white hover:bg-white/20">✕</Button>
            </div>
            <div className="p-4 space-y-3">
              {ewayBills.map((value, index) => {
                const isValid = isValidEway(value)
                return (
                  <div key={index}>
                    <input
                      type="text"
                      value={value}
                      maxLength={12}
                      inputMode="numeric"
                      placeholder="Enter 12-digit E-Way Bill No."
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "")
                        const updated = [...ewayBills]
                        updated[index] = val
                        setEwayBills(updated)
                      }}
                      className={`w-full px-3 py-2 border rounded-xl ${value && !isValid ? "border-red-500" : "border-gray-300"} focus:outline-none`}
                    />
                    {value && !isValid && <p className="text-xs text-red-500 mt-1">E-Way Bill must be exactly 12 digits</p>}
                  </div>
                )
              })}
              <div className="flex gap-2 pt-2">
                <Button variant="secondary" size="sm" disabled={!isValidEway(ewayBills[ewayBills.length - 1])} onClick={() => setEwayBills([...ewayBills, ""])}>Add New</Button>
                <Button variant="danger" size="sm" disabled={ewayBills.length === 1} onClick={() => setEwayBills(ewayBills.slice(0, -1))}>Remove Last</Button>
                <Button variant="success" size="sm" disabled={!ewayBills.some(isValidEway)} onClick={handleSubmitEwayBills}>Submit</Button>
              </div>
            </div>
            <div className="flex justify-end px-4 py-2 border-t">
              <Button variant="secondary" size="sm" onClick={handleCloseEwayBillModal}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {showUpdateVehicleModal && ewaybillFeatureOn && selectedEwayBill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-md">
            <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Update Vehicle / Part-B</h3>
              <Button variant="ghost" onClick={() => { setShowUpdateVehicleModal(false); setSelectedEwayBill(null) }} className="text-white hover:bg-white/20">✕</Button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-gray-500">EWB: {selectedEwayBill.eway_bill_number}</p>
              <div>
                <label className="text-xs font-medium text-gray-600">Vehicle No *</label>
                <input
                  type="text"
                  value={updateVehicleForm.vehicleNo}
                  onChange={(e) => setUpdateVehicleForm((prev) => ({ ...prev, vehicleNo: e.target.value.toUpperCase() }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Reason</label>
                <input
                  type="text"
                  value={updateVehicleForm.reasonRem}
                  onChange={(e) => setUpdateVehicleForm((prev) => ({ ...prev, reasonRem: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-xl"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" size="sm" onClick={() => { setShowUpdateVehicleModal(false); setSelectedEwayBill(null) }}>Close</Button>
                <Button variant="primary" size="sm" loading={ewayActionLoading} disabled={ewayActionLoading} onClick={handleUpdateEwayVehicle}>
                  Update
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddPackageDimensionModal
        isOpen={showAddPackageModal}
        onClose={() => {
          setShowAddPackageModal(false)
          setAddPackageAppendMode(false)
        }}
        existingParcels={parcels}
        appendMode={addPackageAppendMode}
        onSave={setParcels}
        ratePerKg={formData.rate_per_kg}
      />
      <EditPackageDimensionModal
        isOpen={showEditPackageModal}
        onClose={() => setShowEditPackageModal(false)}
        parcels={parcels}
        onSave={setParcels}
        ratePerKg={formData.rate_per_kg}
      />
      <ViewPackageDimensionModal
        isOpen={showViewPackageModal}
        onClose={() => setShowViewPackageModal(false)}
        parcels={parcels}
        ratePerKg={formData.rate_per_kg}
      />
    </DashboardLayout>
  )
}

export default EditShipment;
